import React, { useState } from 'react';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import Collapse from '@mui/material/Collapse';
import Link from '@mui/material/Link';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import NavigationRoutes from '@common/enums/query-params/sphere-navigation-routes';
import { useCookieConsent } from '../../hooks/useCookieConsent';
import { PRIVACY_POLICY_URL } from '../../utils/cookieConsent';

const CookieConsentBox: React.FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const theme = useTheme();
  const matchesSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const [expanded, setExpanded] = useState(false);

  const { consent, accept, decline } = useCookieConsent();

  if (consent !== null) return null;

  const isOnLegalPage = pathname.replace(/\/$/, '').endsWith(NavigationRoutes.Legal);

  const expandClickHandler = () => {
    setExpanded((prev) => !prev)
  };

  const privacyLink = isOnLegalPage ? (
    <Link component={RouterLink} to={{ search: '?t=privacypolicy' }}>
      {t('link-privacy-policy')}
    </Link>
  ) : (
    <Link href={PRIVACY_POLICY_URL} target="_blank" rel="noopener">
      {t('link-privacy-policy')}
    </Link>
  );

  const content = (
    <Stack spacing={2}>
      <Typography>{t('info-cookies-agreement')}</Typography>
      <Typography>
        {t('info-for-more-info-check-privacy-policy')} {privacyLink}
      </Typography>
    </Stack>
  );

  const actions = (
    <React.Fragment>
      <Button variant="text" color="inherit" onClick={decline}>
        {t('button-decline-cookies')}
      </Button>
      <Button variant="contained" onClick={accept}>
        {t('button-accept-cookies')}
      </Button>
    </React.Fragment>
  );

  if (isOnLegalPage) {
    return (
      <Card
        aria-label={t('title-cookies-agreement')}
        sx={{
          position: 'fixed',
          bottom: theme.spacing(matchesSmDown ? 1 : 2),
          right: theme.spacing(matchesSmDown ? 1 : 2),
          left: matchesSmDown ? theme.spacing(1) : 'auto',
          maxWidth: matchesSmDown ? 'none' : 400,
          p: 2,
          boxShadow: 8,
          zIndex: theme.zIndex.snackbar,
        }}
      >
        <Stack spacing={1.5}>
          <ButtonBase
            disabled={!matchesSmDown}
            onClick={expandClickHandler}
            aria-expanded={matchesSmDown ? expanded : undefined}
            sx={{ justifyContent: 'space-between', textAlign: 'left', width: '100%' }}
          >
            <Typography variant="subtitle1" component="h2">
              {t('title-cookies-agreement')}
            </Typography>
            {matchesSmDown && (expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
          </ButtonBase>
          <Collapse in={!matchesSmDown || expanded}>{content}</Collapse>
          <Stack direction="row" justifyContent="flex-end" spacing={1}>
            {actions}
          </Stack>
        </Stack>
      </Card>
    );
  }

  return (
    <Dialog open>
      <DialogTitle>{t('title-cookies-agreement')}</DialogTitle>
      <DialogContent>{content}</DialogContent>
      <DialogActions>{actions}</DialogActions>
    </Dialog>
  );
};

export default CookieConsentBox;
